import { OFFICE_BODIES, OFFICE_CODES, officeBody, type CompanySource, type GovernanceRoleDto, type OfficeCode } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateOnly, personName } from '../../lib/format';
import { useTenant } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, Field, Select } from '../../ui';

const FROM: Partial<Record<CompanySource, string>> = {
  visura: 'visura',
  persona: 'indicato da te',
  documento: 'documento',
  sito: 'sito dell’azienda',
  registro: 'pagina pubblica con la P.IVA',
};

/** Le cariche senza codice, in attesa del riordino (ADR 0124). */
const UNCODED = 'Da riordinare';

const ended = (g: GovernanceRoleDto) => g.stato === 'cessata';
/** Prima chi è in carica, poi dal mandato più recente: data di nomina, poi di fine. */
const recentFirst = (a: GovernanceRoleDto, b: GovernanceRoleDto) =>
  Number(ended(a)) - Number(ended(b)) || (b.nominato_il ?? '').localeCompare(a.nominato_il ?? '') || (b.fine ?? '').localeCompare(a.fine ?? '');

interface Office {
  key: string;
  name: string;
  code: OfficeCode | null;
  items: GovernanceRoleDto[];
}

/** Le cariche per organo e, dentro, per carica (ADR 0124): organi nell'ordine della codifica. */
function byBody(list: GovernanceRoleDto[]): Array<{ body: string; offices: Office[] }> {
  const bodies = new Map<string, Map<string, Office>>();
  for (const g of list) {
    const body = g.codice ? officeBody(g.codice) : UNCODED;
    const key = g.codice ?? g.carica.trim().toLowerCase();
    const offices = bodies.get(body) ?? new Map<string, Office>();
    const o = offices.get(key) ?? { key, name: g.carica, code: g.codice, items: [] };
    o.items.push(g);
    offices.set(key, o);
    bodies.set(body, offices);
  }
  const codes = Object.keys(OFFICE_CODES);
  const rank = (b: string) => (b === UNCODED ? 999 : (OFFICE_BODIES as readonly string[]).indexOf(b));
  return [...bodies]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([body, offices]) => ({
      body,
      offices: [...offices.values()]
        .map((o) => ({ ...o, items: [...o.items].sort(recentFirst) }))
        // Prima le cariche ricoperte, poi nell'ordine della tabella.
        .sort((a, b) => Number(a.items.every(ended)) - Number(b.items.every(ended)) || (a.code && b.code ? codes.indexOf(a.code) - codes.indexOf(b.code) : a.name.localeCompare(b.name))),
    }));
}

/** Dal, al e durata del mandato come si sanno. */
function when(g: GovernanceRoleDto): string {
  const parts = [g.nominato_il ? `dal ${dateOnly(g.nominato_il)}` : null, g.fine ? `al ${dateOnly(g.fine)}` : null].filter(Boolean);
  if (g.durata) parts.push(g.durata);
  if (ended(g) && !g.fine) parts.push('cessata, data non nota');
  return parts.join(' · ');
}

/**
 * Organi e cariche (ADR 0123, 0124): un riquadro per organo, dentro una carica alla volta con chi la ricopre ora, i
 * mandati più recenti per primi; la freccia apre i titolari precedenti di quella carica.
 */
export function Governance({ list }: { list: GovernanceRoleDto[] }) {
  const { t, isManager } = useTenant();
  const qc = useQueryClient();
  const tidy = useMutation({
    mutationFn: () => api<{ codificate: number; tolte: number; unite: number }>(t('/company/governance/tidy'), { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries(),
  });
  const end = useMutation({
    mutationFn: (id: string) => api(t(`/company/governance/${id}`), { method: 'PATCH', body: { stato: 'cessata' } }),
    onSuccess: () => void qc.invalidateQueries(),
  });
  // Una carica letta male (di un'altra impresa, mai esistita) si toglie; una finita si segna cessata.
  const remove = useMutation({
    mutationFn: (id: string) => api(t(`/company/governance/${id}`), { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries(),
  });
  const actions: HolderActions | null = isManager
    ? {
        busy: end.isPending || remove.isPending,
        end: (id) => end.mutate(id),
        remove: (g) => {
          if (window.confirm(`Togliere «${g.carica}» di ${g.titolare}? Usalo per una carica che non è dell’impresa o non è mai esistita; per una carica finita usa «Cessata».`)) remove.mutate(g.id);
        },
      }
    : null;
  const groups = byBody(list);
  const uncoded = list.filter((g) => !g.codice).length;
  return (
    <>
      {isManager && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <Button variant="ghost" disabled={tidy.isPending} onClick={() => tidy.mutate()}>
            {tidy.isPending ? 'Riordino…' : 'Riordina'}
          </Button>
          <span className={tidy.error ? 'text-red-700' : 'text-ink/55'}>
            {tidy.error
              ? `Riordino non fatto, nessuna carica cambiata: ${errorMessage(tidy.error)}`
              : tidy.data
              ? `${tidy.data.codificate} codificate, ${tidy.data.tolte} tolte perché non sono cariche dell’impresa, ${tidy.data.unite} doppioni uniti.`
              : uncoded
                ? `${uncoded === 1 ? '1 carica' : `${uncoded} cariche`} senza codice: il riordino dà a ognuna il codice del Registro delle imprese, toglie quelle che non sono cariche dell’impresa e unisce i doppioni.`
                : 'Ogni carica ha il codice del Registro delle imprese.'}
          </span>
        </div>
      )}
      {!groups.length && <Empty>Nessuna carica nota: arrivano dalla visura, o aggiungile qui sotto.</Empty>}
      <div className="grid gap-4 lg:grid-cols-2">
        {groups.map((g) => (
          <Card key={g.body}>
            <h2 className="mb-1 text-sm font-semibold">{g.body}</h2>
            <div className="divide-y divide-black/5">
              {g.offices.map((o) => (
                <OfficeHistory key={o.key} office={o} actions={actions} />
              ))}
            </div>
          </Card>
        ))}
      </div>
      {isManager && <AddOffice />}
      <ErrorText>{end.error ? errorMessage(end.error) : remove.error ? errorMessage(remove.error) : ''}</ErrorText>
    </>
  );
}

/** Una carica: chi la ricopre ora e, con la freccia, chi l'ha ricoperta prima, dal più recente. */
interface HolderActions {
  busy: boolean;
  end: (id: string) => void;
  remove: (g: GovernanceRoleDto) => void;
}

function OfficeHistory({ office, actions }: { office: Office; actions: HolderActions | null }) {
  const [open, setOpen] = useState(false);
  const current = office.items.filter((g) => !ended(g));
  const before = office.items.filter(ended);
  return (
    <div className="py-2">
      <div className="flex items-center gap-2 text-sm">
        <span className="font-medium">{office.name}</span>
        {office.code && (
          <span className="rounded bg-black/5 px-1.5 font-mono text-[11px] text-ink/60" title="Codice carica del Registro delle imprese">
            {office.code}
          </span>
        )}
        {before.length > 0 && (
          <button className="ml-auto text-xs text-ink/55 hover:text-accent" onClick={() => setOpen(!open)} aria-expanded={open}>
            {open ? '▾' : '▸'} {before.length === 1 ? '1 precedente' : `${before.length} precedenti`}
          </button>
        )}
      </div>
      {!current.length && <p className="text-xs text-ink/50">Nessuno in carica</p>}
      {current.map((g) => (
        <Holder key={g.id} g={g} actions={actions} />
      ))}
      {open && (
        <div className="mt-1 border-l-2 border-black/10 pl-3 opacity-60">
          {before.map((g) => (
            <Holder key={g.id} g={g} actions={actions} />
          ))}
        </div>
      )}
    </div>
  );
}

function Holder({ g, actions }: { g: GovernanceRoleDto; actions: HolderActions | null }) {
  const period = when(g);
  return (
    <div className="flex flex-wrap items-center gap-2 py-0.5 text-sm" title={g.carica_scritta !== g.carica ? `Scritta dalla fonte: ${g.carica_scritta}` : undefined}>
      <span className="flex-1">
        <b className="font-medium">{g.tipo_titolare === 'persona' ? personName(g.titolare) : g.titolare}</b>
        {g.tipo_titolare === 'societa' && <span className="text-ink/50"> (società)</span>}
        {period && <span className="text-ink/60"> · {period}</span>}
        {g.quota && <span className="text-ink/60"> · {/^quota\b/i.test(g.quota) ? g.quota : `quota ${g.quota}`}</span>}
        <span className="text-xs text-ink/40" title={g.rif ?? undefined}>
          {' '}
          · {FROM[g.fonte] ?? g.fonte}
        </span>
        {!g.codice && g.organo && <Badge>{g.organo}</Badge>}
        {g.non_in_visura_dal && !ended(g) && (
          <span title="Letta da un documento: la visura non la riporta. Se è una carica vera tienila, altrimenti «Togli».">
            {' '}
            <Badge tone="amber">non risulta in visura del {dateOnly(g.non_in_visura_dal)}</Badge>
          </span>
        )}
      </span>
      {actions && (
        <span className="flex gap-1">
          {!ended(g) && (
            <Button variant="ghost" disabled={actions.busy} onClick={() => actions.end(g.id)}>
              Cessata
            </Button>
          )}
          <Button variant="ghost" disabled={actions.busy} onClick={() => actions.remove(g)} title="Non è una carica dell’impresa o non è mai esistita">
            Togli
          </Button>
        </span>
      )}
    </div>
  );
}

/** Aggiungere una carica: la carica si sceglie dalla tabella del Registro, con dal e al. */
function AddOffice() {
  const { t } = useTenant();
  const qc = useQueryClient();
  const blank = { titolare: '', tipo: 'persona' as 'persona' | 'societa', codice: '' as OfficeCode | '', dal: '', al: '' };
  const [v, setV] = useState(blank);
  const add = useMutation({
    mutationFn: () =>
      api(t('/company/governance'), {
        body: {
          titolare: v.titolare.trim(),
          tipo_titolare: v.tipo,
          carica: v.codice ? OFFICE_CODES[v.codice] : '',
          codice: v.codice || null,
          nominato_il: v.dal || null,
          fine: v.al || null,
        },
      }),
    onSuccess: () => {
      setV(blank);
      void qc.invalidateQueries();
    },
  });
  const options = OFFICE_BODIES.map((b) => ({ b, codes: (Object.keys(OFFICE_CODES) as OfficeCode[]).filter((c) => officeBody(c) === b) }));
  return (
    <Card className="mt-4">
      <h2 className="mb-2 text-sm font-semibold">Aggiungi una carica</h2>
      <div className="grid gap-2 sm:grid-cols-6">
        <div className="sm:col-span-2">
          <Field label="Titolare" value={v.titolare} onChange={(e) => setV({ ...v, titolare: e.target.value })} />
        </div>
        <Select label="È" value={v.tipo} onChange={(e) => setV({ ...v, tipo: e.target.value as 'persona' | 'societa' })}>
          <option value="persona">una persona</option>
          <option value="societa">una società</option>
        </Select>
        <div className="sm:col-span-3">
          <Select label="Carica" value={v.codice} onChange={(e) => setV({ ...v, codice: e.target.value as OfficeCode | '' })}>
            <option value="">Scegli…</option>
            {options.map((o) => (
              <optgroup key={o.b} label={o.b}>
                {o.codes.map((c) => (
                  <option key={c} value={c}>
                    {OFFICE_CODES[c]} ({c})
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </div>
        <Field label="Dal" type="date" value={v.dal} onChange={(e) => setV({ ...v, dal: e.target.value })} />
        <Field label="Al" type="date" value={v.al} onChange={(e) => setV({ ...v, al: e.target.value })} />
        <div className="flex items-end">
          <Button variant="ghost" disabled={add.isPending || !v.titolare.trim() || !v.codice} onClick={() => add.mutate()}>
            Aggiungi
          </Button>
        </div>
      </div>
      <ErrorText>{add.error ? errorMessage(add.error) : ''}</ErrorText>
    </Card>
  );
}
